import {
  CUSTOM_LOGIN,
  CUSTOM_LOGIN_RESEND_OTP,
  GET_OTP_ON_CALL,
  SEND_EMAIL_OTP,
  HDFC_LOGIN,
  VERIFY_HDFC_OTP,
} from "graphql/customlogin";
import { loginHdfc, loginHdfcVariables } from "graphql/types/loginHdfc";
import {
  validateLoginHdfcOTP,
  validateLoginHdfcOTPVariables,
} from "graphql/types/validateLoginHdfcOTP";
import { getUrlParam } from "helpers/commonHelpers";
import {
  GenerateOtpEmailId,
  GenerateOtpEmailIdVariables,
  GenerateOtpEmailId_GenerateOtpEmailId,
} from "graphql/types/GenerateOtpEmailId";
import {
  getOTPOnCall,
  getOTPOnCallVariables,
} from "graphql/types/getOTPOnCall";
import { LOGIN_TYPE } from "graphql/types/globalTypes";
import { Login, LoginVariables } from "graphql/types/Login";
import { ResendOtp, ResendOtpVariables } from "graphql/types/ResendOtp";
import { dataLayerTracking } from "gtmTracking";
import { useAuth } from "hooks/authHooks";
import { signInUsingAuthService } from "helpers/SSOHelpers";
import _get from "lodash/get";
import React, { useContext, useEffect, useState } from "react";
import { useApolloClient } from "react-apollo-hooks";
import format from "date-fns/format";
import { SAVE_SYMPTOM_CHECKER_INFO } from "graphql/consult";

export interface OTPContextProps {
  sendOtp: ((phoneNumber: string) => Promise<unknown>) | null;
  sendEmailOtp:
    | ((
        email: string,
        partnerId: string
      ) => Promise<GenerateOtpEmailId_GenerateOtpEmailId>)
    | null;
  sendOtpOnCall: ((phoneNumber: string) => Promise<unknown>) | null;
  verifyHdfcOtp:
    | ((id: string, otp: string, token: string, DOB: Date) => Promise<unknown>)
    | null;
  sendHdfcOtp: (phoneNumber: string, DOB: any) => Promise<unknown> | null;
  resendOtp:
    | ((phoneNumber: string, loginId: string) => Promise<unknown>)
    | null;
  sendOtpError: boolean;
  isSendingOtp: boolean;
  verifyOtp:
    | ((otp: string, loginId: string, source: string) => Promise<unknown>)
    | null;
  verifyOtpError: boolean;
  setVerifyOtpError: ((verifyOtpError: boolean) => void) | null;
  isVerifyingOtp: boolean;
}

export const OTPContext = React.createContext<OTPContextProps>({
  sendOtpOnCall: null,
  sendOtp: null,
  sendEmailOtp: null,
  verifyHdfcOtp: null,
  sendHdfcOtp: null,
  resendOtp: null,
  sendOtpError: false,
  isSendingOtp: false,

  verifyOtp: null,
  verifyOtpError: false,
  setVerifyOtpError: null,
  isVerifyingOtp: false,
});

const isBrowser = () => typeof window !== "undefined";
const source = (isBrowser() && getUrlParam("utm_source")) || "";
const token = isBrowser() && getUrlParam("t");

export const OTPProvider: React.FC = (props) => {
  const {
    setCustomLoginId,
    customLoginId,
    setIsSigningIn,
    app,
    wait,
    setSSOToken,
    setAuthToken,
    currentPatient,
  } = useAuth();
  const apolloClient = useApolloClient();
  const [isSendingOtp, setIsSendingOtp] = useState<
    OTPContextProps["isSendingOtp"]
  >(false);
  const [sendOtpError, setSendOtpError] = useState<
    OTPContextProps["sendOtpError"]
  >(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState<
    OTPContextProps["isVerifyingOtp"]
  >(false);
  const [verifyOtpError, setVerifyOtpError] = useState<
    OTPContextProps["verifyOtpError"]
  >(false);

  const sendOtp = async (phoneNumber: string) => {
    await apolloClient
      .mutate<Login, LoginVariables>({
        variables: {
          mobileNumber: phoneNumber,
          loginType: LOGIN_TYPE.PATIENT,
        },
        mutation: CUSTOM_LOGIN,
      })
      .then((loginResult) => {
        setIsSendingOtp(false);
        localStorage.setItem("mobile_num", phoneNumber);
        setCustomLoginId("" || loginResult?.data?.login?.loginId);
      })
      .catch(() => {
        setCustomLoginId("");
      });
  };

  const sendOtpOnCall = (phoneNumber: string) => {
    return new Promise((res, rej) => {
      apolloClient
        .query<getOTPOnCall, getOTPOnCallVariables>({
          variables: {
            mobileNumber: phoneNumber,
            loginType: LOGIN_TYPE.PATIENT,
            id: customLoginId,
          },
          query: GET_OTP_ON_CALL,
          fetchPolicy: "no-cache",
        })
        .then((loginResult) => {
          res(loginResult.data.getOTPOnCall);
          localStorage.setItem("mobile_num", phoneNumber);
          setCustomLoginId(_get(loginResult, "data.getOTPOnCall.loginId", ""));
        })
        .catch((e) => {
          rej(e);
          setCustomLoginId("");
        });
    });
  };

  const resendOtp = (phoneNumber: string, loginId: string) => {
    return new Promise((resolve) => {
      setVerifyOtpError(false);
      setIsSendingOtp(true);
      resendOtpApiCall(phoneNumber, loginId).then((generatedLoginId: any) => {
        setCustomLoginId(generatedLoginId ? generatedLoginId : "");
        resolve(generatedLoginId);
      });
    }).finally(() => {
      setIsSendingOtp(false);
    });
  };

  const verifyOtp = (
    otp: string,
    loginId: string = "",
    source: string = "mobile"
  ) => {
    return new Promise(async (resolve, reject) => {
      setIsSigningIn(true);
      setIsVerifyingOtp(true);
      if (source === "mobile" && otp?.length)
        otpCheckApiCall(otp, loginId).then(async (res: string) => {
          if (!res) {
            setVerifyOtpError(true);
            setIsSigningIn(false);
            dataLayerTracking({
              event: "OTPValidation Failed",
            });
          } else {
            savePatientChatId();
            setVerifyOtpError(false);
            setSSOToken(res);
          }
          setIsVerifyingOtp(false);
          resolve(res);
        });
      else if (source === "email" && otp?.length) {
        otpCheckApiCallForEmail(otp, loginId, "EMAIL").then((res) => {
          if (!res) {
            setVerifyOtpError(true);
            setIsSigningIn(false);
            dataLayerTracking({
              event: "OTPValidation Failed",
            });
          } else {
            savePatientChatId();
            setVerifyOtpError(false);
            setIsSigningIn(false);
            if (res.mobileNumber?.length) setSSOToken(res.authToken);
          }
          setIsVerifyingOtp(false);
          resolve(res);
        });
      }
    });
  };

  const updateMobileNumber = (mobileNumber: string) => {
    if (mobileNumber && isBrowser()) {
      localStorage.setItem("mobile_num", mobileNumber);
    }
  };
  const otpCheckApiCall = async (otp: string, loginId: string) => {
    const verifyLoginOtpResult = await signInUsingAuthService(
      otp,
      loginId,
      "",
      token,
      source
    );
    if (
      verifyLoginOtpResult?.response?.status &&
      verifyLoginOtpResult.response.authToken &&
      !verifyLoginOtpResult.response.isBlocked
    ) {
      updateMobileNumber(verifyLoginOtpResult?.response?.mobileNumber);
      return verifyLoginOtpResult.response.authToken;
    } else return null;
  };

  const otpCheckApiCallForEmail = async (
    otp: string,
    loginId: string,
    source: string
  ) => {
    const verifyLoginOtpResult = await signInUsingAuthService(
      otp,
      loginId,
      source
    );
    if (
      verifyLoginOtpResult?.response?.status &&
      !verifyLoginOtpResult.response.isBlocked
    ) {
      updateMobileNumber(verifyLoginOtpResult?.response?.mobileNumber);
      return verifyLoginOtpResult?.response;
    } else return null;
  };

  const sendEmailOtp = async (
    email: string,
    partnerId: string
  ): Promise<GenerateOtpEmailId_GenerateOtpEmailId> => {
    setIsSendingOtp(true);
    return new Promise((resolve, reject) =>
      apolloClient
        .query<GenerateOtpEmailId, GenerateOtpEmailIdVariables>({
          variables: {
            GenerateOtpEmailIdInput: {
              email: email.trim(),
              partnerId: partnerId,
            },
          },
          query: SEND_EMAIL_OTP,
        })
        .then((response) => {
          setIsSendingOtp(false);
          resolve(response.data.GenerateOtpEmailId);
        })
        .catch((err) => console.error("Corporate Email otp error", err))
    );
  };

  const resendOtpApiCall = async (mobileNumber: string, loginId: string) => {
    const [resendOtpResult, resendOtpError] = await wait(
      apolloClient.mutate<ResendOtp, ResendOtpVariables>({
        variables: {
          mobileNumber: mobileNumber,
          id: loginId,
          loginType: LOGIN_TYPE.PATIENT,
        },
        mutation: CUSTOM_LOGIN_RESEND_OTP,
      })
    );
    setIsSendingOtp(false);
    if (
      resendOtpResult?.data?.resendOtp?.status &&
      resendOtpResult.data.resendOtp.loginId
    ) {
      setSendOtpError(false);
      return resendOtpResult.data.resendOtp.loginId;
    } else if (resendOtpError) {
      setSendOtpError(true);
      return false;
    } else {
      setSendOtpError(true);
      return false;
    }
  };

  const savePatientChatId = async () => {
    const askApolloData: any =
      sessionStorage?.getItem("symptomCheckerPaidConsult")?.length > 0
        ? JSON.parse(sessionStorage?.getItem("symptomCheckerPaidConsult"))
        : {};
    if (Object.keys(askApolloData)?.length > 0) {
      await apolloClient
        .mutate({
          variables: {
            patientAskApolloInfo: {
              chatId: askApolloData?.id,
              mobileNumber: localStorage.getItem("mobile_num") || "",
            },
          },
          mutation: SAVE_SYMPTOM_CHECKER_INFO,
        })
        .then((data: any) => {
          sessionStorage.removeItem("symptomCheckerPaidConsult");
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {});
    }
  };

  const sendHdfcOtp = async (phoneNumber: string, DOB: any) => {
    setIsSendingOtp(true);
    const date = format(new Date(DOB), "yyyy MMMM dd");
    if (isBrowser()) localStorage.setItem("dob", date);
    let obj = {};
    await apolloClient
      .mutate<loginHdfc, loginHdfcVariables>({
        variables: {
          mobileNumber: phoneNumber,
          DOB: date,
        },
        mutation: HDFC_LOGIN,
      })
      .then((loginResult: any) => {
        obj = {
          id: loginResult.data.loginHdfc.id,
          token: loginResult.data.loginHdfc.token,
          status: loginResult.data.loginHdfc.status,
          responseCode: loginResult.data.loginHdfc.responseCode,
        };
        setIsSendingOtp(false);
        if (isBrowser()) localStorage.setItem("mobile_num", phoneNumber);
      })
      .catch(() => {
        console.log("error while connecting send otp");
      });
    return obj;
  };

  const verifyHdfcOtp = (id: string, otp: string, token: string, DOB: Date) => {
    return new Promise((resolve, reject) => {
      setIsSigningIn(true);
      setIsVerifyingOtp(true);
      const date = format(new Date(DOB), "yyyy MMMM dd");
      apolloClient
        .mutate<validateLoginHdfcOTP, validateLoginHdfcOTPVariables>({
          variables: {
            id: id,
            otp: otp,
            token: token,
            DOB: date,
          },
          mutation: VERIFY_HDFC_OTP,
        })
        .then((response: any) => {
          let data = response.data.validateLoginHdfcOTP;
          let token: any = false;
          if (data && data.status && data.authToken && !data.isBlocked) {
            token = data.authToken;
          }
          if (!token) {
            setVerifyOtpError(true);
            setIsSigningIn(false);
            /**Gtm code start start */
            dataLayerTracking({
              event: "OTPValidation Failed",
            });
            /**Gtm code start end */
          } else {
            setVerifyOtpError(false);
            app.auth().signInWithCustomToken(token);
          }
          setIsVerifyingOtp(false);
          resolve(data);
        });
    });
  };
  return (
    <OTPContext.Provider
      value={{
        sendEmailOtp,
        sendOtp,
        sendOtpOnCall,
        resendOtp,
        sendOtpError,
        isSendingOtp,
        verifyOtp,
        verifyOtpError,
        setVerifyOtpError,
        isVerifyingOtp,
        verifyHdfcOtp,
        sendHdfcOtp,
      }}
    >
      {props.children}
    </OTPContext.Provider>
  );
};

const useOTPContext = () => useContext<OTPContextProps>(OTPContext);

export const useOTP = () => {
  const verifyOtp = useOTPContext().verifyOtp!;
  const verifyOtpError = useOTPContext().verifyOtpError!;
  const setVerifyOtpError = useOTPContext().setVerifyOtpError!;
  const isVerifyingOtp = useOTPContext().isVerifyingOtp!;

  const sendOtp = useOTPContext().sendOtp!;
  const sendEmailOtp = useOTPContext().sendEmailOtp;
  const sendOtpOnCall = useOTPContext().sendOtpOnCall!;
  const resendOtp = useOTPContext().resendOtp!;
  const sendOtpError = useOTPContext().sendOtpError;
  const isSendingOtp = useOTPContext().isSendingOtp;
  return {
    verifyOtp,
    verifyOtpError,
    setVerifyOtpError,
    isVerifyingOtp,
    sendOtp,
    sendEmailOtp,
    sendOtpOnCall,
    resendOtp,
    sendOtpError,
    isSendingOtp,
  };
};
